import { ApiError } from '../../lib/api';
import {
  AUTO_RESOLVE_BATCH_STATUSES, AUTO_RESOLVE_BATCH_RESULTS, AUTO_RESOLVE_BATCH_BUCKETS,
} from '../../config/autoResolve';

// Batch auto-resolve (docs/auto-resolve-batch.md, EXPERIMENTAL) — the pieces
// the per-school panel (AutoResolveBatchPanel), the all-schools "Batch runs"
// strip (AutoResolveBatchStrip), the per-batch detail card
// (AutoResolveBatchCard) and the bulk dialog (AutoResolveBatchDialog) share:
// status groups, the stepper mapping, per-request result rows, the
// attention-first split (partitionBatches), safe links, dates, chip tones and
// button styles. No JSX here.
//
// Every colour class below already exists in the console (StatusBadge, the
// per-ticket panel), so the generated light theme covers it — see
// docs/light-mode.md before adding a new one.

const {
  PREPARING, DISPATCHED, RUNNING, PR_OPEN, NEEDS_HUMAN_ONLY, MERGED, DISCARDED, FAILED,
} = AUTO_RESOLVE_BATCH_STATUSES;
const { DONE, NEEDS_HUMAN, SKIPPED } = AUTO_RESOLVE_BATCH_RESULTS;
const { AUTO, REVIEW, HUMAN } = AUTO_RESOLVE_BATCH_BUCKETS;

// Poll cadence: fast while the agent works, slower while a PR waits for review.
export const BATCH_POLL_ACTIVE_MS = 6000;
export const BATCH_POLL_REVIEW_MS = 12000;

export const WORKING = [PREPARING, DISPATCHED, RUNNING];
// A request in a batch with one of these statuses can't join another batch.
export const LOCKING = [...WORKING, PR_OPEN];
// The run is over: missing per-request results now mean "not reported".
export const TERMINAL = [NEEDS_HUMAN_ONLY, MERGED, DISCARDED, FAILED];
// Nothing left to do on these.
export const FINAL = [MERGED, DISCARDED];
export const DISCARDABLE = [PR_OPEN, NEEDS_HUMAN_ONLY, FAILED];
// Queue rows that offer Dismiss (the server also accepts discarded, which is
// folded anyway).
export const DISMISSABLE = [NEEDS_HUMAN_ONLY, FAILED];

export const NOT_REPORTED = 'not_reported';
export const WAITING = 'waiting';

export function batchPollDelay(batches) {
  const statuses = (batches || []).map((b) => b?.status);
  if (statuses.some((s) => WORKING.includes(s))) return BATCH_POLL_ACTIVE_MS;
  if (statuses.includes(PR_OPEN)) return BATCH_POLL_REVIEW_MS;
  return 0;
}

export const STEP_LABELS = ['Dispatched', 'Editing', 'Review', 'Published'];

function stepsFrom(states) {
  return STEP_LABELS.map((label, i) => ({ label, state: states[i] }));
}

// The run stopped at step `index`: that step carries the terminal label.
function stoppedAt(index, state, label) {
  return STEP_LABELS.map((stepLabel, i) => {
    if (i < index) return { label: stepLabel, state: 'complete' };
    if (i === index) return { label, state };
    return { label: stepLabel, state: 'skipped' };
  });
}

// Dispatched → Editing → Review → Published, or the terminal state where the
// run stopped (Needs a person / Failed / Discarded).
export function batchSteps(batch) {
  switch (batch?.status) {
    case PREPARING: return stepsFrom(['current', 'upcoming', 'upcoming', 'upcoming']);
    case DISPATCHED:
    case RUNNING: return stepsFrom(['complete', 'current', 'upcoming', 'upcoming']);
    case PR_OPEN: return stepsFrom(['complete', 'complete', 'current', 'upcoming']);
    case MERGED: return stepsFrom(['complete', 'complete', 'complete', 'complete']);
    case NEEDS_HUMAN_ONLY: return stoppedAt(2, 'needs_person', 'Needs a person');
    case DISCARDED: return stoppedAt(batch.pr_url ? 2 : 1, 'discarded', 'Discarded');
    case FAILED: return stoppedAt(batch.pr_url ? 2 : 1, 'failed', 'Failed');
    default: return stepsFrom(['upcoming', 'upcoming', 'upcoming', 'upcoming']);
  }
}

// D1 keeps JSON as TEXT; accept an array or its JSON string.
export function asArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    try { const parsed = JSON.parse(v); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
  }
  return [];
}

// `results` is the stored results.json (§6) or just its `results` array;
// null until the workflow has reported.
export function batchResults(batch) {
  let raw = batch?.results;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw); } catch { return null; }
  }
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.results)) return raw.results;
  return null;
}

// One row per request in the batch: done / needs_human / skipped, or
// not_reported (no entry once the run is over) / waiting (still running).
export function batchRequestRows(batch) {
  const results = batchResults(batch);
  const byId = new Map();
  for (const r of results || []) if (r && r.id != null) byId.set(String(r.id), r);
  const requestIds = asArray(batch?.request_ids).map(String);
  const ids = requestIds.length ? requestIds : [...byId.keys()];
  const over = results !== null || TERMINAL.includes(batch?.status);
  return ids.map((id) => {
    const r = byId.get(id) || null;
    if (r && [DONE, NEEDS_HUMAN, SKIPPED].includes(r.status)) return { id, status: r.status, result: r };
    return { id, status: over ? NOT_REPORTED : WAITING, result: r };
  });
}

// done / needs a person / left (everything else: waiting, skipped, not reported).
export function batchCounts(batch) {
  const rows = batchRequestRows(batch);
  const done = rows.filter((r) => r.status === DONE).length;
  const needsPerson = rows.filter((r) => r.status === NEEDS_HUMAN).length;
  return { total: rows.length, done, needsPerson, left: rows.length - done - needsPerson };
}

// ---- Attention first -------------------------------------------------------------
// The strip and the panel list only the batches that still want something —
// working (preparing / dispatched / running), waiting for review (pr_open),
// needs a person or failed — unless dismissed (acknowledged_at, POST
// { action: 'dismiss' }) or superseded (superseded_by: a later batch of the
// school holds every one of its requests). Everything else folds into one
// summary row with Show all.

export const FOLD = {
  PUBLISHED: 'published',
  DISMISSED: 'dismissed',
  SUPERSEDED: 'superseded',
  DISCARDED: 'discarded',
};

// Summary-row wording. `reasonChip`: the folded row carries a chip saying
// why (`why` is its tooltip and screen-reader text); Published and Discarded
// rows already say it with their status chip.
export const FOLD_META = {
  [FOLD.PUBLISHED]: { label: 'Published', count: (n) => `${n} published` },
  [FOLD.DISMISSED]: {
    label: 'Dismissed', count: (n) => `${n} dismissed`, reasonChip: true,
    why: 'taken out of the queue by hand; Restore puts it back',
  },
  [FOLD.SUPERSEDED]: {
    label: 'Superseded', count: (n) => `${n} superseded`, reasonChip: true,
    why: 'a later batch included every one of its requests',
  },
  [FOLD.DISCARDED]: { label: 'Discarded', count: (n) => `${n} discarded` },
};
const FOLD_ORDER = [FOLD.PUBLISHED, FOLD.DISMISSED, FOLD.SUPERSEDED, FOLD.DISCARDED];

export const ATTENTION = {
  RUNNING: 'running',
  REVIEW: 'review',
  PERSON: 'person',
  FAILED: 'failed',
};
const ATTENTION_TEXT = {
  [ATTENTION.RUNNING]: (n) => `${n} running`,
  [ATTENTION.REVIEW]: (n) => `${n} to review`,
  [ATTENTION.PERSON]: (n) => `${n} ${n === 1 ? 'needs' : 'need'} a person`,
  [ATTENTION.FAILED]: (n) => `${n} failed`,
};
const ATTENTION_ORDER = [ATTENTION.RUNNING, ATTENTION.REVIEW, ATTENTION.PERSON, ATTENTION.FAILED];

export function isDismissed(batch) {
  return Boolean(batch?.acknowledged_at);
}

export function isSuperseded(batch) {
  return batch?.superseded_by != null && batch.superseded_by !== '';
}

// Why a batch is folded out of the queue (a FOLD value), or null while it
// still wants something. A batch that is working or waiting for review is
// always in the queue; an unknown status stays in the queue too.
export function foldReason(batch) {
  const status = batch?.status;
  if (status === MERGED) return FOLD.PUBLISHED;
  if (LOCKING.includes(status)) return null;
  if (isDismissed(batch)) return FOLD.DISMISSED;
  if (isSuperseded(batch)) return FOLD.SUPERSEDED;
  if (status === DISCARDED) return FOLD.DISCARDED;
  return null;
}

// What a queued batch wants (an ATTENTION value), or null for an unknown status.
export function attentionOf(batch) {
  const status = batch?.status;
  if (WORKING.includes(status)) return ATTENTION.RUNNING;
  if (status === PR_OPEN) return ATTENTION.REVIEW;
  if (status === NEEDS_HUMAN_ONLY) return ATTENTION.PERSON;
  if (status === FAILED) return ATTENTION.FAILED;
  return null;
}

// → { queue, folded, counts }. Both lists keep the input order; counts has a
// number for every ATTENTION and FOLD value.
export function partitionBatches(batches) {
  const queue = [];
  const folded = [];
  const counts = Object.fromEntries([...ATTENTION_ORDER, ...FOLD_ORDER].map((k) => [k, 0]));
  for (const b of Array.isArray(batches) ? batches : []) {
    if (!b) continue;
    const reason = foldReason(b);
    if (reason) {
      folded.push(b);
      counts[reason] += 1;
    } else {
      queue.push(b);
      const wants = attentionOf(b);
      if (wants) counts[wants] += 1;
    }
  }
  return { queue, folded, counts };
}

// "2 running · 3 to review · 4 need a person · 1 failed" — zero parts left
// out; '' when the queue is empty.
export function queueSummary(counts) {
  return ATTENTION_ORDER.filter((k) => counts?.[k] > 0).map((k) => ATTENTION_TEXT[k](counts[k])).join(' · ');
}

// "11 published · 2 dismissed · 4 superseded · 1 discarded" — zero parts left out.
export function foldedSummary(counts) {
  return FOLD_ORDER.filter((k) => counts?.[k] > 0).map((k) => FOLD_META[k].count(counts[k])).join(' · ');
}

// Only http(s) links; anything else renders as plain text.
export function safeHttpUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(String(url));
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch {
    return null;
  }
}

// `preview_url + page`, kept on the preview's own origin (the page path comes
// from the agent's results, so "@evil.example" or "//evil.example" never link).
export function previewPageUrl(previewUrl, page) {
  const base = safeHttpUrl(previewUrl);
  const path = String(page || '');
  if (!base || !path.startsWith('/') || path.startsWith('//')) return null;
  try {
    const origin = new URL(base).origin;
    const u = new URL(origin + path);
    return u.origin === origin ? u.href : null;
  } catch {
    return null;
  }
}

export function parseWhen(iso) {
  if (!iso) return null;
  const s = String(iso).replace(' ', 'T');
  const d = new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatWhen(iso) {
  const d = parseWhen(iso);
  return d ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}

export function ageLabel(iso) {
  const d = parseWhen(iso);
  if (!d) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day old';
  if (days < 14) return `${days} days old`;
  return `${Math.floor(days / 7)} weeks old`;
}

export function plural(n, word, many = `${word}s`) {
  return `${n} ${n === 1 ? word : many}`;
}

export function newestFirst(a, b) {
  const ta = parseWhen(a?.created_at)?.getTime() ?? 0;
  const tb = parseWhen(b?.created_at)?.getTime() ?? 0;
  return tb - ta;
}

export const UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

export const CHANGE_TYPE_LABELS = {
  text: 'Text', image: 'Image', link: 'Link', new_section: 'New section', remove: 'Remove', other: 'Other',
};

// Chip colours reuse the console's existing tones (StatusBadge / the per-ticket
// panel), so the generated light theme already covers every class here.
export const CHIP = 'inline-flex items-center shrink-0 px-2 py-0.5 rounded-full border text-[10px] font-medium uppercase tracking-wide whitespace-nowrap';
export const TONE = {
  neutral: 'bg-white/[0.04] border-white/[0.08] text-gray-300',
  amber: 'text-amber-200 bg-amber-500/10 border-amber-500/25',
  grey: 'bg-white/[0.02] border-white/[0.04] text-gray-500',
  lime: 'text-[#B0FC38] bg-[#B0FC38]/10 border-[#B0FC38]/25',
  red: 'text-red-300 bg-red-500/10 border-red-500/25',
  pending: 'bg-white/[0.04] border-white/[0.1] text-gray-400',
  quiet: 'bg-white/[0.03] border-white/[0.08] text-gray-400',
};

// `label` in the per-school panel; `short` on the Edit Requests rows and in
// the bulk dialog.
export const BUCKET_META = {
  [AUTO]: { label: 'Auto', short: 'Auto', tone: 'neutral' },
  [REVIEW]: { label: 'Review', short: 'Review', tone: 'amber' },
  [HUMAN]: { label: 'For a person', short: 'Person', tone: 'grey' },
};

export const STATUS_META = {
  [PREPARING]: { label: 'Preparing', tone: 'amber', sentence: 'is being prepared' },
  [DISPATCHED]: { label: 'Queued', tone: 'amber', sentence: 'was sent and is waiting for the agent to start' },
  [RUNNING]: { label: 'Editing', tone: 'amber', sentence: 'is being edited by the agent' },
  [PR_OPEN]: { label: 'Ready to review', tone: 'lime', sentence: 'is ready to review and publish' },
  [NEEDS_HUMAN_ONLY]: { label: 'Needs a person', tone: 'amber', sentence: 'finished, and every request needs a person' },
  [MERGED]: { label: 'Published', tone: 'lime', sentence: 'was published to the live site' },
  [DISCARDED]: { label: 'Discarded', tone: 'grey', sentence: 'was discarded' },
  [FAILED]: { label: 'Failed', tone: 'red', sentence: 'failed' },
};

export const RESULT_META = {
  [DONE]: { label: 'Done', tone: 'lime' },
  [NEEDS_HUMAN]: { label: 'Needs a person', tone: 'amber' },
  [SKIPPED]: { label: 'Skipped', tone: 'grey' },
  [NOT_REPORTED]: { label: 'Not reported', tone: 'quiet' },
  [WAITING]: { label: 'Waiting', tone: 'pending' },
};

// Why a school can't take a batch (GET …/auto-resolve-batch → problems), in
// plain English for the bulk dialog.
export const PROBLEM_TEXT = {
  no_repo: 'no repo mapped',
  workflow_missing: 'batch workflow not installed on the repo',
  workflow_unverified: 'couldn’t check the repo for the batch workflow (GitHub didn’t answer)',
  github_unconfigured: 'GitHub access isn’t set up on the portal',
  r2_presign_unconfigured: 'file links for the agent aren’t set up on the portal',
  hook_secret_unconfigured: 'the results callback isn’t set up on the portal',
  storage_unbound: 'file storage isn’t connected to the portal',
};

export function problemsText(problems) {
  const list = Array.isArray(problems) ? problems : [];
  const parts = list.map((p) => PROBLEM_TEXT[p] || String(p).replace(/_/g, ' '));
  return parts.length ? parts.join('; ') : 'not set up for batch mode';
}

export const SECTION_HEADING = 'text-[10px] font-medium text-gray-500 tracking-wider uppercase';
export const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60';
export const PRIMARY_BTN = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#B0FC38] text-gray-900 text-[12px] font-semibold hover:bg-[#9BE22E] disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS}`;
export const SECONDARY_BTN = `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] text-gray-400 hover:text-gray-200 border border-white/[0.08] hover:bg-white/[0.04] transition-colors disabled:opacity-60 ${FOCUS}`;
export const QUIET_BTN = `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[12px] text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-60 ${FOCUS}`;
export const DANGER_BTN = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 text-[12px] font-medium hover:bg-red-500/10 disabled:opacity-60 ${FOCUS}`;
export const ERROR_BOX = 'px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[12px] text-red-300/90';
export const EXTERNAL_LINK = `inline-flex items-center gap-1 text-gray-500 hover:text-white rounded ${FOCUS}`;

export function errorText(err, fallback) {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

export function isDisabledResponse(err) {
  return err instanceof ApiError && err.status === 404
    && (err.code === 'disabled' || err.data?.code === 'disabled');
}

// A remembered boolean (collapsed state). Storage can be unavailable (private
// window, blocked site data): then nothing is remembered and `fallback` wins.
export function readStoredFlag(key, fallback = false) {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === 'true';
  } catch {
    return fallback;
  }
}

export function writeStoredFlag(key, value) {
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Storage unavailable: not remembered.
  }
}
